import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Grid, MenuItem, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { loanApi } from '../../../api/loanApi';
import {
  isNeverDisbursed, LoanListItemDto, LoanStatus, LoanStatusColors, LoanStatusLabels, LoanSummaryDto,
  LoanType, LoanTypeLabels,
} from '../../../types/loanTypes';
import LoanFormDialog from '../components/LoanFormDialog';

const money = (value: number) =>
  `₹${(value ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const SummaryCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Paper sx={{ p: 2 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h6" fontWeight={700}>{value}</Typography>
  </Paper>
);

const LoansPage: React.FC = () => {
  const navigate = useNavigate();
  const [loans, setLoans] = useState<LoanListItemDto[]>([]);
  const [summary, setSummary] = useState<LoanSummaryDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([
        loanApi.getLoans({
          status: statusFilter ? (Number(statusFilter) as LoanStatus) : undefined,
          loanType: typeFilter ? (Number(typeFilter) as LoanType) : undefined,
          search: search || undefined,
        }),
        loanApi.getSummary(),
      ]);
      setLoans(list);
      setSummary(sum);
    } catch {
      toast.error('Failed to load loans');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, search]);

  useEffect(() => {
    const handle = setTimeout(() => void load(), 250);
    return () => clearTimeout(handle);
  }, [load]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={700}>Loans & Advances</Typography>
        <Button variant="contained" startIcon={<AddIcon />} sx={{ textTransform: 'none' }} onClick={() => setDialogOpen(true)}>
          New Loan / Advance
        </Button>
      </Stack>

      {summary && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 6, md: 3 }}><SummaryCard label="Active Loans" value={String(summary.activeLoans)} /></Grid>
          <Grid size={{ xs: 6, md: 3 }}><SummaryCard label="Pending Requests" value={String(summary.pendingRequests)} /></Grid>
          <Grid size={{ xs: 6, md: 3 }}><SummaryCard label="Total Outstanding" value={money(summary.totalOutstanding)} /></Grid>
          <Grid size={{ xs: 6, md: 3 }}><SummaryCard label="Recovery This Month" value={money(summary.expectedRecoveryThisMonth)} /></Grid>
        </Grid>
      )}

      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField size="small" label="Search" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 220 }} />
          <TextField select size="small" label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 170 }}>
            <MenuItem value="">All</MenuItem>
            {Object.entries(LoanStatusLabels).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="">All</MenuItem>
            {Object.entries(LoanTypeLabels).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
          </TextField>
        </Stack>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Reference</TableCell>
                  <TableCell>Employee</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Principal</TableCell>
                  <TableCell align="right">Outstanding</TableCell>
                  <TableCell align="center">Installments</TableCell>
                  <TableCell>Recovery From</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loans.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align="center">No loans found</TableCell></TableRow>
                ) : loans.map((loan) => (
                  <TableRow
                    key={loan.id} hover sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/loans/${loan.id}`)}
                  >
                    <TableCell>{loan.referenceNumber}</TableCell>
                    <TableCell>
                      {loan.employeeName}
                      <Typography variant="caption" color="text.secondary" display="block">{loan.employeeCode}</Typography>
                    </TableCell>
                    <TableCell>{LoanTypeLabels[loan.loanType]}</TableCell>
                    <TableCell align="right">{money(loan.principalAmount)}</TableCell>
                    <TableCell align="right">
                      {isNeverDisbursed(loan.status)
                        ? <Typography variant="body2" color="text.disabled">—</Typography>
                        : money(loan.outstandingBalance)}
                    </TableCell>
                    <TableCell align="center">{loan.installmentsPaid}/{loan.totalInstallments}</TableCell>
                    <TableCell>{MONTHS[loan.repaymentStartMonth - 1]} {loan.repaymentStartYear}</TableCell>
                    <TableCell>
                      <Chip size="small" label={LoanStatusLabels[loan.status]} color={LoanStatusColors[loan.status]} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <LoanFormDialog open={dialogOpen} mode="hr" onClose={() => setDialogOpen(false)} onSaved={load} />
    </Box>
  );
};

export default LoansPage;
